// Auto-scroll hook for chat messages
//
// Three separate questions, kept separate on purpose:
//
//  1. Is the reader FOLLOWING? — `userFollowing`. Their intent, and only they
//     can change it, by scrolling. Tracked from a real `scroll` listener,
//     because that is the only event a person's decision to leave the bottom
//     actually produces.
//  2. May we scroll right now? — `shouldAutoScroll()`, a pure function of (1)
//     plus policy: "read from top" while a reply body streams, overridden by the
//     thinking / tool-call activity row, which is the only thing telling the
//     user the assistant is busy and so has to stay in view.
//  3. Did the page grow AFTER we decided? — images. An <img> is inserted with no
//     intrinsic height and grows the scroller when it decodes, which is neither
//     a DOM mutation nor a scroll: nothing fires, and the reader has been pushed
//     up the page without touching anything. So we re-scroll when it settles.
//
// These used to be one boolean. `shouldAutoScroll()` fused intent, policy and
// the activity override, and its answer was then frozen at insert time and
// replayed seconds later at image load as if it had meant "the reader was
// following". During body streaming there is no activity row, so the fused
// answer was always false, so EVERY image in a streaming reply was bound with
// following=false and the settle-scroll never fired — precisely the case the
// binding exists for. Asking (1) directly at load time is both simpler and
// honest: page growth does not move `scrollTop`, so it cannot fire a scroll
// event, so it cannot change the reader's recorded intent.
//
// Related history: (1) used to be implemented as "no auto-scroll at all while
// data-streaming", which killed the activity row outright. We let the indicator
// win whenever the user is following, and scroll instantly rather than smoothly
// while a generation is live — the flashing/jitter that the blanket block was
// added to fix came from repeated *smooth* scrolls retargeting each other, not
// from scrolling as such.

// How close to the bottom counts as "the user is following along".
const NEAR_BOTTOM_THRESHOLD_PX = 120;

// How long a scroll WE started may still be in flight. A smooth scroll fires
// `scroll` repeatedly on its way down, from positions that are nowhere near the
// bottom; read naively, our own scrolling would be mistaken for the user
// scrolling away and would switch auto-scroll off. Within this window a scroll
// event can only confirm following, never revoke it. Comfortably longer than a
// smooth scroll takes, and irrelevant while streaming, where we scroll
// instantly and the very next event lands at the bottom and closes it.
const AUTO_SCROLL_SETTLE_MS = 600;

// The element AutoScroll measures to size the message list's bottom padding, and
// the property it publishes the measurement as. Both halves of a contract with
// the composer (see `InputArea`) and the scroller's own `padding-bottom`, so
// they are named here rather than spelled inline: a rename that misses one end
// leaves the composer permanently covering the last message.
const INPUT_AREA_SELECTOR = '[data-input-area]';
const INPUT_HEIGHT_PROPERTY = '--wb-input-height';

const AutoScroll = {
  mounted() {
    // Flag to track if this is the first scroll (on mount)
    this.isFirstScroll = true;

    // We open at the bottom, so the reader starts out following.
    this.userFollowing = true;
    this.pendingAutoScroll = false;
    this.boundImages = new WeakSet();
    // Every image listener we add hangs off this, so teardown removes the lot in
    // one call — the alternative is keeping a strong reference to every <img>
    // this scroller has ever seen, which is a leak in a long conversation.
    this.imageListeners = new AbortController();

    // Scroll to bottom immediately without animation on first load
    this.scrollToBottom(false);

    this.onScroll = () => this.recordScrollIntent();
    this.el.addEventListener('scroll', this.onScroll, { passive: true });

    // Images already on screen at mount (a reloaded conversation) still have to
    // be bound: the observer below only ever sees what arrives after it.
    for (const img of this.el.querySelectorAll('img')) { this.bindImage(img); }

    // Auto-scroll on content changes
    this.observer = new MutationObserver((mutations) => {
      if (this.shouldAutoScroll()) {
        this.scrollToBottom(true);
      }

      this.bindAddedImages(mutations);
    });

    this.observer.observe(this.el, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Watch for changes in input area (e.g., attachment previews, textarea expansion)
    const inputArea = document.querySelector(INPUT_AREA_SELECTOR);
    if (inputArea) {
      // Update CSS variable with input area height
      //
      // The following test MUST be taken before the variable is written. The
      // scroller pads its own bottom by the published height, so growing the
      // composer — attaching a file is the big one, the preview is ~200px —
      // grows `scrollHeight` by the same amount. Measured after the write, this
      // asked "are they following along?" about a layout its own line had just
      // changed and decided no. (Reading `userFollowing` rather than
      // re-measuring makes that ordering less critical than it was, but the
      // scroll it triggers still wants the new padding in place first.)
      const updateInputHeight = () => {
        const height = inputArea.offsetHeight;
        const following = this.shouldAutoScroll();

        document.documentElement.style.setProperty(
          INPUT_HEIGHT_PROPERTY,
          `${height}px`
        );

        if (following) {
          this.scrollToBottom(true);
        }
      };

      // Initial height set
      updateInputHeight();

      // Attributes on the element ITSELF, and nothing else. The composer hides
      // itself with a class while a scripted onboarding step owns the turn, and
      // that is a change to the watched element, invisible to a childList
      // observer — without it the message list keeps padding the bottom by the
      // height of a composer that is no longer on screen. Everything else that
      // used to be watched here (children arriving, the box growing as it is
      // typed into) changes the element's SIZE, which the ResizeObserver below
      // already reports; subtree+attributes meant every keystroke's class change
      // on any descendant re-measured the composer and forced a reflow.
      this.inputObserver = new MutationObserver(() => {
        updateInputHeight();
      });

      this.inputObserver.observe(inputArea, {
        attributes: true,
        attributeFilter: ['class']
      });

      // Watch for size changes (textarea expansion, attachments arriving)
      this.resizeObserver = new ResizeObserver(() => {
        updateInputHeight();
      });

      this.resizeObserver.observe(inputArea);
    } else {
      // Loud, because the failure is silent otherwise: the padding falls back to
      // a guess forever and the composer sits on top of the last message.
      console.warn(
        `[AutoScroll] no ${INPUT_AREA_SELECTOR} element — the message list will ` +
        `fall back to its default bottom padding and the composer may cover the ` +
        `last message`
      );
    }

    // Handle explicit scroll events from LiveView
    this.handleEvent('scroll_to_bottom', () => {
      // Use requestAnimationFrame to ensure DOM has updated before scrolling
      requestAnimationFrame(() => {
        this.scrollToBottom(true);
      });
    });

    // Handle scroll to specific message (for new assistant messages)
    // Scrolls to show the top of the message with offset so user can see
    // the end of their sent message plus the beginning of the response
    this.handleEvent('scroll_to_message', ({ id }) => {
      requestAnimationFrame(() => {
        const messageEl = document.getElementById(id);
        if (messageEl) {
          // Measure against the scroller itself rather than offsetTop, which is
          // relative to the nearest positioned ancestor (currently <main>) and
          // only happens to line up because the toolbar is fixed and the
          // composer absolute.
          const offset =
            this.el.scrollTop +
            (messageEl.getBoundingClientRect().top - this.el.getBoundingClientRect().top) -
            100;
          // Deliberately NOT shielded as one of our own scrolls. This one parks
          // the reader UP the page on purpose, and where it leaves them is the
          // truth about whether they are still at the bottom: park them halfway
          // up a long reply and they are not following the tail any more (the
          // activity row should not yank them back down), park them a few pixels
          // up a short one and they still are. Letting the resulting scroll
          // events record that is exactly right, and needs no special case.
          this.el.scrollTo({
            top: Math.max(0, offset),
            behavior: 'smooth'
          });
          // Hold this position while the reply body streams so the user can
          // read from the top.
          this.streamingMessageId = id;
        }
      });
    });

    // Re-enable auto-scroll when streaming completes
    this.handleEvent('streaming_complete', () => {
      this.streamingMessageId = null;
    });

    // Mark that initial scroll is complete
    setTimeout(() => {
      this.isFirstScroll = false;
    }, 100);
  },

  destroyed() {
    this.el.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
    this.inputObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.imageListeners?.abort();
    clearTimeout(this.autoScrollSettleTimer);
  },

  // The reader scrolled. This is the ONLY thing that changes `userFollowing`:
  // content growing below them does not move `scrollTop`, so it cannot reach
  // here and cannot be mistaken for them choosing to leave the bottom.
  recordScrollIntent() {
    const nearBottom = this.isNearBottom();

    if (nearBottom) {
      // Either they came back, or one of our own scrolls arrived. Both end the
      // in-flight window.
      this.pendingAutoScroll = false;
      this.userFollowing = true;
      return;
    }

    // Mid-flight of a scroll we started: not the reader's doing.
    if (this.pendingAutoScroll) { return; }

    this.userFollowing = false;
  },

  // Bind every <img> that arrived in this batch of mutations.
  //
  // Driven off `addedNodes` rather than re-querying the whole list: the markdown
  // renderer rewrites a streaming reply's innerHTML on a 50ms debounce, so a
  // full rescan was walking every image in the conversation ~20 times a second.
  // For the same reason there is no "already bound" MARKER in the DOM — the
  // elements are thrown away and rebuilt wholesale, so a marker survives nothing
  // and buys nothing. The WeakSet is belt and braces for the case where the same
  // element is re-added.
  bindAddedImages(mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) { continue; }

        if (node.tagName === 'IMG') {
          this.bindImage(node);
        } else {
          for (const img of node.querySelectorAll('img')) { this.bindImage(img); }
        }
      }
    }
  },

  // Re-scroll once a newly-inserted image finishes decoding, if the reader is
  // still following at that point.
  //
  // Asked at LOAD time, not at insert time. `userFollowing` is not affected by
  // the image growing the scroller, so the answer is as true then as now — and
  // if they scrolled away in the meantime, it correctly says so.
  bindImage(img) {
    // Already decoded (cached): it contributed its height before we measured, so
    // whatever scrolling was due has already accounted for it.
    if (img.complete) { return; }
    if (this.boundImages.has(img)) { return; }
    this.boundImages.add(img);

    // `error` as well as `load`: a broken image still resolves to its alt box
    // and still changes the height.
    const settle = () => {
      if (this.userFollowing) { this.scrollToBottom(true); }
    };
    const opts = { once: true, signal: this.imageListeners.signal };
    img.addEventListener('load', settle, opts);
    img.addEventListener('error', settle, opts);
  },

  isStreaming() {
    return this.el.dataset.streaming === 'true';
  },

  // True while the thinking / tool-call progress row is on screen. That row is
  // the only thing telling the user the assistant is busy, so it takes priority
  // over the read-from-top hold.
  hasActivityIndicator() {
    return this.el.querySelector('[data-chat-activity]') !== null;
  },

  isNearBottom() {
    return (
      this.el.scrollHeight - this.el.scrollTop - this.el.clientHeight <
      NEAR_BOTTOM_THRESHOLD_PX
    );
  },

  // Pure function of the reader's intent and our display policy. Deliberately
  // measures no scroll geometry: re-measuring is how the old version kept
  // answering a question its own scrolling had already changed.
  shouldAutoScroll() {
    // They scrolled away. Nothing outranks that.
    if (!this.userFollowing) { return false; }

    // Keep the activity row in view — including mid-reply, when the model pauses
    // to run a tool.
    if (this.hasActivityIndicator()) { return true; }

    // Reply body is streaming: hold position so the user reads from the top.
    if (this.streamingMessageId || this.isStreaming()) { return false; }

    return true;
  },

  // Open the window in which `scroll` events are ours rather than the reader's.
  //
  // Only worth opening for a long trip down: that is the one that fires events
  // from far-from-bottom positions on its way. Starting from the bottom there is
  // nothing to misread, and arming anyway would swallow a genuine scroll away
  // for the length of the window — the reader would be dragged back by the next
  // mutation. Any event that arrives near the bottom closes it early.
  markOwnScroll() {
    if (this.isNearBottom()) { return; }

    this.pendingAutoScroll = true;
    clearTimeout(this.autoScrollSettleTimer);
    this.autoScrollSettleTimer = setTimeout(() => {
      this.pendingAutoScroll = false;
    }, AUTO_SCROLL_SETTLE_MS);
  },

  scrollToBottom(animate = true) {
    // Never animate mid-generation: the content below changes on every token
    // and overlapping smooth scrolls are what read as flashing/jitter.
    const smooth = animate && !this.isFirstScroll && !this.isStreaming();
    this.markOwnScroll();
    this.el.scrollTo({
      top: this.el.scrollHeight,
      behavior: smooth ? 'smooth' : 'instant'
    });
  }
};

export default AutoScroll;
